import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { Info } from "lucide-react";

export const HighestConsumingAssets = ({ data }: any) => {
  const currency = useCurrency();
  const facilityData = data?.entityLevel?.topFacility;
  const systemData = data?.entityLevel?.topSystem;
  const networkData = data?.entityLevel?.topNetwork;
  // Mock data for highest consuming assets
  const consumingAssets = [
    {
      title: "Highest Consuming Facility",
      name: `${facilityData?.name ?? "NA"}`,
      consumption: `${facilityData?.consumptionValue?.display ?? "NA"}`,
      percentage: `${facilityData?.percentageOfTotal?.display ?? "NA"}`,
      monthlyCost: `${currency} ${facilityData?.monthlyCost?.value ?? "NA"}`,
      efficiency: `${facilityData?.efficiency?.display ?? "NA"}`,
      tooltip: (
        <ul>
          <li>
            Calculates consumption for all facilities:
            <ul>
              <li>
                For each facility:
                <ul>
                  <li>Get all systems within the facility</li>
                  <li>
                    For each system, sum all asset readings using BR-006 logic
                  </li>
                  <li>Sums all system totals to get facility total</li>
                </ul>
              </li>
            </ul>
          </li>

          <li>Ranks all facilities by total consumption in descending order</li>

          <li>Selects Rank 1 (highest consuming) facility</li>
        </ul>
      ),
    },
    {
      title: "Highest Consuming System",
      name: `${systemData?.name ?? "NA"}`,
      consumption: `${systemData?.consumptionValue?.display ?? "NA"}`,
      percentage: `${systemData?.percentageOfTotal?.display ?? "NA"}`,
      monthlyCost: `${currency} ${systemData?.monthlyCost?.value ?? "NA"}`,
      efficiency: `${systemData?.efficiency?.display ?? "NA"}`,
      tooltip: (
        <ul>
          <li>
            Calculates consumption for all systems:
            <ul>
              <li>
                For each system:
                <ul>
                  <li>
                    Sums all asset readings within that system using BR-006
                    logic
                  </li>
                </ul>
              </li>
            </ul>
          </li>

          <li>Ranks all systems by total consumption in descending order</li>

          <li>Selects Rank 1 (highest consuming) system</li>
        </ul>
      ),
    },
    {
      title: "Highest Consuming Network",
      name: `${networkData?.name ?? "NA"}`,
      consumption: `${networkData?.consumptionValue?.display ?? "NA"}`,
      percentage: `${networkData?.percentageOfTotal?.display ?? "NA"}`,
      monthlyCost: `${currency} ${networkData?.monthlyCost?.value ?? "NA"}`,
      efficiency: `${networkData?.efficiency?.display ?? "NA"}`,
      tooltip: (
        <ul>
          <li>
            Process:
            <ul>
              <li>
                Calculates consumption for all networks:
                <ul>
                  <li>
                    For each network:
                    <ul>
                      <li>Sum all asset readings tagged to that network </li>
                    </ul>
                  </li>
                </ul>
              </li>

              <li>
                Ranks all networks by total consumption in descending order
              </li>

              <li>Selects Rank 1 (highest consuming) network</li>
            </ul>
          </li>
        </ul>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Highest Consuming Assets</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {consumingAssets.map((asset, index) => (
          <Card key={index} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base font-medium text-muted-foreground flex items-center gap-2">
                  {asset.title}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{asset.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Asset Name */}
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {asset.name}
                </p>
                <p className="text-3xl font-bold text-blue-600">
                  {asset.consumption}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {asset.percentage}
                </p>
              </div>

              {/* Metrics */}
              <div className="space-y-2 pt-3 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Monthly Cost:
                  </span>
                  <span className="text-sm font-semibold">
                    {asset.monthlyCost}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Efficiency:
                  </span>
                  <span className="text-sm font-semibold">
                    {asset.efficiency}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
